import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Grid,
  IconButton,
  Tooltip,
  Slide,
  InputAdornment,
} from "@mui/material";
import { Edit, Delete, Search } from "@mui/icons-material";
import AdminSidebar from "./AdminSidebar";

// Import BASE_URL from your backend configuration
import { BASE_URL } from "../backend/backend.js";

const SellManager = () => {
  const [sales, setSales] = useState([
    {
      sellId: 1,
      productName: "Aspirin",
      quantity: 10,
      price: 20,
      totalPrice: 200,
      date: "2025-04-10",
    },
  ]);

  const [formData, setFormData] = useState({
    productName: "",
    quantity: "",
    price: "",
    totalPrice: "",
    date: "",
  });

  const [editId, setEditId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prevData) => {
      let newValue = value;

      // Calculate total price dynamically
      if (name === "quantity" || name === "price") {
        const updatedQuantity = name === "quantity" ? value : formData.quantity;
        const updatedPrice = name === "price" ? value : formData.price;
        const total = parseFloat(updatedQuantity) * parseFloat(updatedPrice);
        return { ...prevData, [name]: value, totalPrice: isNaN(total) ? "" : total.toString() };
      }

      return { ...prevData, [name]: newValue };
    });
  };

  const handleAddOrUpdate = () => {
    if (editId !== null) {
      const updatedSales = sales.map((sale) =>
        sale.sellId === editId ? { ...sale, ...formData, sellId: editId } : sale
      );
      setSales(updatedSales);
      setEditId(null);
    } else {
      const newSale = {
        sellId: sales.length + 1,
        ...formData,
      };
      setSales([...sales, newSale]);
    }

    setFormData({
      productName: "",
      quantity: "",
      price: "",
      totalPrice: "",
      date: "",
    });
  };

  const handleEdit = (sale) => {
    setFormData(sale);
    setEditId(sale.sellId);
  };

  const handleDelete = (id) => {
    const updatedSales = sales.filter((sale) => sale.sellId !== id);
    setSales(updatedSales);
    if (editId === id) {
      setEditId(null);
      setFormData({
        productName: "",
        quantity: "",
        price: "",
        totalPrice: "",
        date: "",
      });
    }
  };

  const filteredSales = sales.filter(
    (sale) =>
      sale.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sale.date.includes(searchTerm)
  );

  return (
    <Box sx={{ display: "flex" }}>
      <AdminSidebar />

      <Box sx={{ flexGrow: 1, padding: 4 }}>
        <Typography variant="h4" sx={{ textAlign: "center", marginBottom: 4 }}>
          Sales Management
        </Typography>

        <Grid container spacing={4}>
          {/* Form Section */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ padding: 3 }}>
              <Typography variant="h6" gutterBottom>
                {editId ? "Edit Sale" : "Add New Sale"}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Product Name"
                    name="productName"
                    value={formData.productName}
                    onChange={handleChange}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Quantity"
                    name="quantity"
                    type="number"
                    value={formData.quantity}
                    onChange={handleChange}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Price"
                    name="price"
                    type="number"
                    value={formData.price}
                    onChange={handleChange}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Total Price"
                    name="totalPrice"
                    type="number"
                    value={formData.totalPrice}
                    disabled
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Date"
                    name="date"
                    type="date"
                    value={formData.date}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6} display="flex" alignItems="center">
                  <Button
                    variant="contained"
                    color={editId ? "warning" : "primary"}
                    onClick={handleAddOrUpdate}
                    fullWidth
                  >
                    {editId ? "Update Sale" : "Add Sale"}
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Table Section */}
          <Grid item xs={12} md={8}>
            <Slide direction="down" in={true} mountOnEnter unmountOnExit>
              <Box mb={2}>
                <TextField
                  label="Search by Product Name or Date"
                  variant="outlined"
                  fullWidth
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Slide>

            <Paper>
              <TableContainer>
                <Table sx={{ minWidth: 650 }} aria-label="sale table">
                  <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableRow>
                      <TableCell>Sell ID</TableCell>
                      <TableCell>Product Name</TableCell>
                      <TableCell>Quantity</TableCell>
                      <TableCell>Price</TableCell>
                      <TableCell>Total Price</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredSales.map((sale) => (
                      <TableRow key={sale.sellId}>
                        <TableCell>{sale.sellId}</TableCell>
                        <TableCell>{sale.productName}</TableCell>
                        <TableCell>{sale.quantity}</TableCell>
                        <TableCell>{sale.price}</TableCell>
                        <TableCell>{sale.totalPrice}</TableCell>
                        <TableCell>{sale.date}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="Edit">
                            <IconButton
                              color="primary"
                              onClick={() => handleEdit(sale)}
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              color="error"
                              onClick={() => handleDelete(sale.sellId)}
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredSales.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          No sales found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default SellManager;